import { KeyboardEvent } from 'react';
import { PremadeAnswer } from 'flow/types/Chats';

// The state kept by the premade answer inserter.
export type PremadeAnswerInserterState =
    | {
          open: false;
      }
    | {
          open: true;
          // Text after the / to use in filtering
          filter: string;
          // The current proposed insertion, if any.
          proposedInsertion?: PremadeAnswer;
          // If we're using arrow navigation, then the offset of the item to insert (set on
          // key move, validated and the proposed insertion sync'd by the component).
          insertionOffset?: number;
      };

// Initial premade answer inserter state.
export const getInitialPremadeAnswerInserterState =
    (): PremadeAnswerInserterState => ({
        open: false,
    });

// Function to calculate the premade answer inserter state based upon the message
// input text area.
export const getNextPremadeAnswerInserterState = (
    currentState: PremadeAnswerInserterState,
    input: HTMLTextAreaElement,
): PremadeAnswerInserterState => {
    // Ensure it starts with a `/`.
    const message = input.value;
    if (!message.startsWith('/')) {
        return { open: false };
    }

    // Ensure there's at most a single word after it.
    const cursorPosition = input.selectionStart;
    const words = message.slice(1, cursorPosition).split(/\s+/);
    if (words.length > 1) {
        return { open: false };
    }

    // Candidate for the premade answer inserter.
    return {
        ...currentState,
        open: true,
        filter: words[0],
    };
};

// This is called when there is a keystroke in the message input, and gives the mention inserter
// chance to process or act upon it. Returns true if there should be no further processing.
export const offerKeyEventToPremadeAnswerInserter = (
    event: KeyboardEvent<HTMLTextAreaElement>,
    state: PremadeAnswerInserterState,
    updateState: (update: PremadeAnswerInserterState) => void,
    updateMessage: (update: string, cursorPosition: number) => void,
    substitutePlaceholders: (value: string) => string,
) => {
    // If it's not open or there's no proposed insertion, nothing to do.
    if (!state.open || !state.proposedInsertion) {
        return false;
    }

    // Otherwise, go by key.
    switch (event.key) {
        case 'Enter': {
            // Insert the premade answer and close.
            insertPremadeAnswer(
                substitutePlaceholders(state.proposedInsertion.message),
                updateState,
                updateMessage,
            );
            event.preventDefault();
            return true;
        }
        case 'Escape': {
            // Close the answer inserter.
            updateState({ open: false });
            return true;
        }
        case 'ArrowUp': {
            updateState({
                ...state,
                insertionOffset: state.insertionOffset - 1,
            });
            event.preventDefault();
            return true;
        }
        case 'ArrowDown': {
            updateState({
                ...state,
                insertionOffset: state.insertionOffset + 1,
            });
            event.preventDefault();
            return true;
        }
    }
    return false;
};

export const insertPremadeAnswer = (
    premadeAnswer: string,
    updateState: (update: PremadeAnswerInserterState) => void,
    updateMessage: (update: string, cursorPosition: number) => void,
) => {
    updateMessage(premadeAnswer, premadeAnswer.length);
    updateState({ open: false });
};
